import React, { useState, useRef } from 'react';
import { 
  MoreVertical, 
  Edit, 
  Trash2, 
  UserCheck, 
  UserX, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  Info
} from 'lucide-react';
import { User as UserType } from '../../services/userManagementService';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Dropdown from '../UI/Dropdown';
import { formatFullNameWithTitle } from '../../utils/guestNameUtils';

interface UserCardProps {
  user: UserType;
  onEdit: (user: UserType) => void;
  onDelete: (user: UserType) => void;
  onToggleStatus: (user: UserType) => void;
  onShowDetails?: (user: UserType) => void;
  actionLoading?: boolean;
  getRoleColor: (role: string) => string;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onDelete,
  onToggleStatus,
  onShowDetails,
  actionLoading = false,
  getRoleColor
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLastLogin = (lastLogin?: string) => {
    if (!lastLogin) return 'Never';
    return new Date(lastLogin).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    const isActive = user.is_active;
    const statusConfig = {
      active: {
        label: 'Active',
        className: theme === 'dark' ? 'bg-[#33FF00]/20 text-[#33FF00] border border-[#33FF00]/30' : 'bg-mirsal-green text-mirsal-black',
        dotClassName: 'bg-mirsal-green'
      },
      inactive: {
        label: 'Inactive',
        className: theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-700/50' : 'bg-red-100 text-red-800',
        dotClassName: 'bg-red-500'
      }
    };

    const config = statusConfig[isActive ? 'active' : 'inactive'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${config.dotClassName}`}></span>
        {config.label}
      </span>
    );
  };


  const dropdownItems = [
    {
      label: 'Edit User',
      icon: Edit,
      onClick: () => {
        onEdit(user);
        setIsDropdownOpen(false);
      }
    },
    // Add More Details button for guests (including hosts-as-guests)
    ...((user.role === 'guest' || user.roles?.includes('guest')) && onShowDetails ? [{
      label: 'More Details',
      icon: Info,
      onClick: () => {
        onShowDetails(user);
        setIsDropdownOpen(false);
      },
      className: 'text-blue-600'
    }] : []),
    {
      label: user.is_active ? 'Deactivate' : 'Activate',
      icon: user.is_active ? UserX : UserCheck,
      onClick: () => {
        onToggleStatus(user);
        setIsDropdownOpen(false);
      },
      className: user.is_active ? 'text-red-600' : 'text-green-600'
    },
    {
      label: 'Delete User',
      icon: Trash2,
      onClick: () => {
        onDelete(user);
        setIsDropdownOpen(false);
      },
      className: 'text-red-600'
    }
  ];

  return (
    <div className={`rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all duration-200 h-full flex flex-col ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border-gray-700/50 hover:border-[#33FF00]/30' 
        : 'bg-white border-gray-200 hover:border-[#33FF00]/20'
    }`}>
      {/* Header - Compact */}
      <div className="p-3 sm:p-4 pb-2 sm:pb-3 flex-none">
        <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-1.5 sm:mb-2">
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <span className={`text-xs sm:text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {user.first_name[0]}{user.last_name[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-sm sm:text-base font-semibold mb-0.5 sm:mb-1 leading-tight ${
                  theme === 'dark' ? 'text-white' : 'text-mirsal-black'
                }`}>
                  <span className="line-clamp-2">{formatFullNameWithTitle(user.title, user.first_name, user.last_name)}</span>
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 min-h-[16px] flex-wrap">
              {/* Display all roles from roles array, or fallback to primary role */}
              {(user.roles && user.roles.length > 0 ? user.roles : [user.role]).map((role) => (
                <span key={role} className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
                  <span className="capitalize">{role}</span>
                </span>
              ))}
              {getStatusBadge()}
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1 ml-2 sm:ml-3 flex-shrink-0">
            <div className="relative">
              <Button
                ref={triggerRef}
                variant="ghost"
                size="sm"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={actionLoading}
                className="p-1"
              >
                <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              
              <Dropdown
                isOpen={isDropdownOpen}
                onClose={() => setIsDropdownOpen(false)}
                triggerRef={triggerRef as any}
                items={dropdownItems}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content Area - Compact */}
      <div className="px-3 sm:px-4 pb-2 flex-1 flex flex-col justify-between">
        {/* Contact Information */}
        <div className="mb-2 sm:mb-3">
          <div className="grid grid-cols-1 gap-1.5 sm:gap-2 text-xs">
            {user.email && (
              <div className={`flex items-center ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <Mail className={`h-3 w-3 mr-1 sm:mr-1.5 flex-shrink-0 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                }`} />
                <span className="truncate">{user.email}</span>
              </div>
            )}
            
            <div className={`flex items-center ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <Phone className={`h-3 w-3 mr-1 sm:mr-1.5 flex-shrink-0 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <span className="truncate">{user.phone}</span>
            </div>
          </div>
        </div>

        {/* Additional Info - Compact */}
        <div className="space-y-1.5 sm:space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Language:</span>
            <span className={`uppercase ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>{user.preferred_language}</span>
          </div>
          
          
          <div className="flex items-center justify-between text-xs">
            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>Last Login:</span>
            <span className={`truncate ml-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{formatLastLogin(user.last_login)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`px-3 sm:px-4 py-2 sm:py-3 border-t ${
        theme === 'dark' 
          ? 'bg-gray-800/50 border-gray-700' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className={`flex items-center justify-between text-xs ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <div className="flex items-center min-w-0 flex-1">
            <Calendar className={`h-3 w-3 mr-1 flex-shrink-0 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <span className="truncate">Joined {formatDate(user.created_at)}</span>
          </div>
          
          {user.is_staff && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ml-2 flex-shrink-0 ${
              theme === 'dark' 
                ? 'bg-purple-900/30 text-purple-300 border border-purple-700/50' 
                : 'bg-purple-100 text-purple-800'
            }`}>
              <Shield className="h-2.5 w-2.5 mr-1" />
              Staff
            </span>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {actionLoading && (
        <div className={`absolute inset-0 bg-opacity-75 flex items-center justify-center rounded-lg ${
          theme === 'dark' ? 'bg-gray-900' : 'bg-white'
        }`}>
          <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${
            theme === 'dark' ? 'border-[#33FF00]' : 'border-primary-600'
          }`}></div>
        </div>
      )}
    </div>
  );
};

export default UserCard;
